import type {
  FinalizedPaymentContract,
  CaptureReadinessContract,
  FinancialQueueItemContract,
  FinancialSyncOutcomeContract,
  FinancialSyncSubmissionContract,
  IssuanceSnapshotContract,
  LocalFinancialResultContract,
  ObligationContract,
  PaymentMethodContract,
  ReplicaChangePageContract,
  ReplicaSnapshotPageContract,
  StudentProfileContract,
  StudentReplicaContract,
  SignedSyncLeaseContract,
} from "../../contracts/src/index.js";
import {
  LOCAL_SYNTHETIC_LEASE_PUBLIC_KEY,
  syncLeaseSigningValue,
} from "../../contracts/src/index.js";
import { dateOnly, wholeMad } from "../../domain/academics-obligations/src/index.js";
import { createIssuanceSnapshot } from "../../domain/documents/src/index.js";
import {
  OFFLINE_PIN_FAILURE_LIMIT,
  OFFLINE_PIN_ITERATIONS,
  isValidOfflinePin,
} from "../../domain/identity-access/src/index.js";
import {
  normalizeMassar,
  normalizeStudentSearch,
} from "../../domain/students-enrollment/src/index.js";
import {
  canonicalEnvelope,
  canonicalJson,
  clockContext,
  paymentReference,
  uuidV4,
  validateMethodIntent,
  validateReceivedDate,
  type CanonicalValue,
  type FinalizedPayment,
} from "../../domain/payments-instruments/src/index.js";

export const LOCAL_STORE_DATABASE_NAME = "chams-pay-phase-01";
const DATABASE_VERSION = 3;
const STORE_METADATA = "metadata";
const STORE_ENCRYPTED = "encrypted";
const STORE_KEYS = "keys";
const STORE_COUNTERS = "financial-counters";
const STORE_PAYMENTS = "financial-payments";
const STORE_ALLOCATIONS = "financial-allocations";
const STORE_DOCUMENTS = "financial-documents";
const STORE_ENVELOPES = "financial-envelopes";
const STORE_QUEUE = "financial-queue";
const STORE_AUDIT = "financial-audit";
const STORE_REPLICA_PAGES = "replica-pages";
const STORE_SYNC = "sync-state";
const REPLICA_AAD = new TextEncoder().encode("chams-pay|phase-01|student-replica|v1");
const WRAP_AAD = new TextEncoder().encode("chams-pay|phase-01|data-key-wrap|v1");
const DEVICE_RESUME_AAD = new TextEncoder().encode("chams-pay|device-resume|v1");
const MAXIMUM_SERVER_CLOCK_LEAD_MS = 60_000;
const MAXIMUM_CLOCK_DRIFT_MS = 5 * 60_000;

interface LocalMetadata {
  readonly schemaVersion: 1 | 2 | 3;
  readonly method: "pin" | "prf";
  readonly salt: string;
  readonly iterations: number;
  readonly wrapIv: string;
  readonly wrappedKey: string;
  readonly passkey?: StoredPasskeyWrap;
  readonly deviceResume?: {
    readonly iv: string;
    readonly ciphertext: string;
  };
  readonly replicaIv: string;
  readonly synchronizedAt: string;
  readonly failedAttempts: number;
  readonly retryAfter: number;
  readonly blocked: boolean;
}

interface StoredPasskeyWrap {
  readonly credentialId: string;
  readonly rpId: string;
  readonly prfSalt: string;
  readonly wrapIv: string;
  readonly wrappedKey: string;
}

export interface PasskeyUnlockMaterial {
  readonly credentialId: string;
  readonly rpId: string;
  readonly prfSalt: Uint8Array<ArrayBuffer>;
  readonly prfOutput: Uint8Array<ArrayBuffer>;
}

export interface PasskeyUnlockConfiguration {
  readonly credentialId: string;
  readonly rpId: string;
  readonly prfSalt: Uint8Array<ArrayBuffer>;
}

export interface LocalSyncState {
  readonly schemaVersion: "phase-03-v1";
  readonly initialSyncComplete: boolean;
  readonly persistenceGranted: boolean;
  readonly storageHeadroom: boolean;
  readonly durabilityTestPassed: boolean;
  readonly health: "healthy" | "recovery-required";
  readonly authorized: boolean;
  readonly cursor: string;
  readonly lease?: SignedSyncLeaseContract;
  readonly greatestServerTime?: string;
  readonly observedWallAt?: number;
  readonly observedMonotonicAt?: number;
  readonly clockOffsetMilliseconds?: number;
}

interface StoredEnvelope {
  readonly version: "phase-02-v1" | "phase-03-v1";
  readonly bytes: string;
  readonly digest: string;
  readonly signature: string;
  readonly actorUserId?: string;
  readonly installationId?: string;
}

interface InstallationKeys {
  readonly privateKey: CryptoKey;
  readonly publicKey: CryptoKey;
}

interface EncryptedFinancialRecord {
  readonly iv: string;
  readonly ciphertext: ArrayBuffer;
}

export interface FinalizeFinancialPaymentInput {
  readonly student: {
    readonly id: string;
    readonly nameAr: string;
    readonly nameFr: string;
    readonly classLabel: string;
  };
  readonly obligations: readonly ObligationContract[];
  readonly amountMad: string;
  readonly receivedDate: string;
  readonly today: string;
  readonly activeFrom: string;
  readonly activeThrough: string;
  readonly backdatingReason?: string;
  readonly note?: string;
  readonly method: PaymentMethodContract;
  readonly allocations: readonly { readonly obligationId: string; readonly amountMad: string }[];
  readonly actorUserId: string;
  readonly deploymentId?: string;
  readonly trustedObservedAt: string;
  readonly clientCreatedAt?: string;
  readonly readinessNow?: number;
  readonly readinessMonotonicNow?: number;
}

export interface FinancialCommitTestHooks {
  readonly beforeTransaction?: "termination" | "counter-conflict";
  readonly duringTransaction?: "abort" | "quota" | "write";
}

export class OfflineUnlockError extends Error {
  constructor(
    readonly code:
      "INVALID_PIN" | "THROTTLED" | "ONLINE_REAUTHORIZATION_REQUIRED" | "CORRUPT_CACHE",
  ) {
    super(code);
  }
}

function encode(value: Uint8Array): string {
  let binary = "";
  for (const byte of value) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/g, "");
}

function decode(value: string): Uint8Array<ArrayBuffer> {
  const padded = value
    .replaceAll("-", "+")
    .replaceAll("_", "/")
    .padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = atob(padded);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(length));
}

async function pinWrappingKey(
  pin: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations = OFFLINE_PIN_ITERATIONS,
) {
  if (!isValidOfflinePin(pin)) throw new OfflineUnlockError("INVALID_PIN");
  const material = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(pin),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", hash: "SHA-256", iterations, salt },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

async function prfWrappingKey(output: Uint8Array<ArrayBuffer>) {
  const context = new Uint8Array(
    new TextEncoder().encode("chams-pay-prf-wrap-v1").length + output.length,
  );
  context.set(new TextEncoder().encode("chams-pay-prf-wrap-v1"));
  context.set(output, context.length - output.length);
  const digest = await crypto.subtle.digest("SHA-256", context);
  return crypto.subtle.importKey("raw", digest, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

export async function encryptReplicaForTest(
  pin: string,
  replica: StudentReplicaContract,
  salt = new Uint8Array(16).fill(7),
  wrapIv = new Uint8Array(12).fill(8),
  replicaIv = new Uint8Array(12).fill(9),
) {
  const dataKey = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, [
    "encrypt",
    "decrypt",
  ]);
  const wrappingKey = await pinWrappingKey(pin, salt);
  const rawDataKey = await crypto.subtle.exportKey("raw", dataKey);
  const wrappedKey = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: wrapIv, additionalData: WRAP_AAD },
    wrappingKey,
    rawDataKey,
  );
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: replicaIv, additionalData: REPLICA_AAD },
    dataKey,
    new TextEncoder().encode(JSON.stringify(replica)),
  );
  return {
    wrappedKey: new Uint8Array(wrappedKey),
    ciphertext: new Uint8Array(ciphertext),
    salt,
    wrapIv,
    replicaIv,
  };
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(LOCAL_STORE_DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      for (const store of [
        STORE_METADATA,
        STORE_ENCRYPTED,
        STORE_KEYS,
        STORE_COUNTERS,
        STORE_PAYMENTS,
        STORE_ALLOCATIONS,
        STORE_DOCUMENTS,
        STORE_ENVELOPES,
        STORE_QUEUE,
        STORE_AUDIT,
        STORE_REPLICA_PAGES,
        STORE_SYNC,
      ])
        if (!database.objectStoreNames.contains(store)) database.createObjectStore(store);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function readStore<T>(store: string, key: string): Promise<T | undefined> {
  const database = await openDatabase();
  try {
    return await new Promise<T | undefined>((resolve, reject) => {
      const request = database.transaction(store, "readonly").objectStore(store).get(key);
      request.onsuccess = () => resolve(request.result as T | undefined);
      request.onerror = () => reject(request.error);
    });
  } finally {
    database.close();
  }
}

async function writeStores(
  values: readonly { store: string; key: string; value: unknown }[],
): Promise<void> {
  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(
        [...new Set(values.map((item) => item.store))],
        "readwrite",
        { durability: "strict" },
      );
      for (const item of values) transaction.objectStore(item.store).put(item.value, item.key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () =>
        reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
    });
  } finally {
    database.close();
  }
}

async function deleteStores(values: readonly { store: string; key: string }[]): Promise<void> {
  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(
        [...new Set(values.map((item) => item.store))],
        "readwrite",
        { durability: "strict" },
      );
      for (const item of values) transaction.objectStore(item.store).delete(item.key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () =>
        reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
    });
  } finally {
    database.close();
  }
}

async function allStoreValues<T>(store: string): Promise<readonly T[]> {
  const database = await openDatabase();
  try {
    return await new Promise<readonly T[]>((resolve, reject) => {
      const request = database.transaction(store, "readonly").objectStore(store).getAll();
      request.onsuccess = () => resolve(request.result as readonly T[]);
      request.onerror = () => reject(request.error);
    });
  } finally {
    database.close();
  }
}

function financialAad(kind: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(`chams-pay|phase-02|financial|${kind}|v1`);
}

async function encryptFinancialRecord(
  dataKey: CryptoKey,
  kind: string,
  value: unknown,
): Promise<EncryptedFinancialRecord> {
  const iv = randomBytes(12);
  return {
    iv: encode(iv),
    ciphertext: await crypto.subtle.encrypt(
      { name: "AES-GCM", iv, additionalData: financialAad(kind) },
      dataKey,
      new TextEncoder().encode(JSON.stringify(value)),
    ),
  };
}

async function decryptFinancialRecord<T>(
  dataKey: CryptoKey,
  kind: string,
  record: EncryptedFinancialRecord,
): Promise<T> {
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: decode(record.iv), additionalData: financialAad(kind) },
    dataKey,
    record.ciphertext,
  );
  return JSON.parse(new TextDecoder().decode(plaintext)) as T;
}

function deepFreeze<T>(value: T): T {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const nested of Object.values(value as Record<string, unknown>)) deepFreeze(nested);
  }
  return value;
}

function defaultSyncState(): LocalSyncState {
  return {
    schemaVersion: "phase-03-v1",
    initialSyncComplete: false,
    persistenceGranted: false,
    storageHeadroom: false,
    durabilityTestPassed: false,
    health: "healthy",
    authorized: false,
    cursor: "0",
  };
}

function nextSyncState(
  previous: LocalSyncState,
  lease: SignedSyncLeaseContract,
  readiness: {
    readonly persistenceGranted: boolean;
    readonly storageHeadroom: boolean;
    readonly authorized: boolean;
  },
  now = Date.now(),
  monotonicNow = typeof performance === "undefined" ? 0 : performance.now(),
): LocalSyncState {
  const greatestServerTime =
    previous.greatestServerTime &&
    Date.parse(previous.greatestServerTime) > Date.parse(lease.issuedAt)
      ? previous.greatestServerTime
      : lease.issuedAt;
  const serverAnchor = Date.parse(greatestServerTime);
  const clockOffsetMilliseconds = Number.isFinite(serverAnchor) ? serverAnchor - now : 0;
  return {
    ...previous,
    initialSyncComplete: true,
    persistenceGranted: readiness.persistenceGranted,
    storageHeadroom: readiness.storageHeadroom,
    durabilityTestPassed: true,
    health: "healthy",
    authorized: readiness.authorized,
    cursor: lease.cursor,
    lease,
    greatestServerTime,
    observedWallAt: now + clockOffsetMilliseconds,
    observedMonotonicAt: monotonicNow,
    clockOffsetMilliseconds,
  };
}

async function verifySyncLease(lease: SignedSyncLeaseContract): Promise<boolean> {
  if (lease.keyId !== "phase-03-local-synthetic") return false;
  try {
    const publicKey = await crypto.subtle.importKey(
      "spki",
      decode(LOCAL_SYNTHETIC_LEASE_PUBLIC_KEY),
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"],
    );
    const { signature, ...unsigned } = lease;
    return crypto.subtle.verify(
      { name: "ECDSA", hash: "SHA-256" },
      publicKey,
      decode(signature),
      new TextEncoder().encode(syncLeaseSigningValue(unsigned)),
    );
  } catch {
    return false;
  }
}

export async function assessCaptureReadiness(
  state: LocalSyncState,
  installation: string | null,
  counter: bigint,
  now = Date.now(),
  monotonicNow = typeof performance === "undefined" ? 0 : performance.now(),
): Promise<CaptureReadinessContract> {
  if (!state.initialSyncComplete) return { allowed: false, code: "INITIAL_SYNC_REQUIRED" };
  if (!state.persistenceGranted) return { allowed: false, code: "PERSISTENCE_REQUIRED" };
  if (!state.storageHeadroom) return { allowed: false, code: "STORAGE_HEADROOM_REQUIRED" };
  if (!state.durabilityTestPassed) return { allowed: false, code: "DURABILITY_TEST_REQUIRED" };
  if (state.health !== "healthy") return { allowed: false, code: "RECOVERY_REQUIRED" };
  if (!state.authorized || !installation) return { allowed: false, code: "AUTHORIZATION_REQUIRED" };
  if (counter >= 999_999n) return { allowed: false, code: "COUNTER_EXHAUSTED" };
  if (!state.lease || !(await verifySyncLease(state.lease)))
    return { allowed: false, code: "CLOCK_UNCERTAIN" };
  if (state.lease.installationId !== installation)
    return { allowed: false, code: "AUTHORIZATION_REQUIRED" };
  const issuedAt = Date.parse(state.lease.issuedAt);
  const expiresAt = Date.parse(state.lease.expiresAt);
  const highWater = Date.parse(state.greatestServerTime ?? state.lease.issuedAt);
  if (![issuedAt, expiresAt, highWater].every(Number.isFinite) || expiresAt < issuedAt)
    return { allowed: false, code: "CLOCK_UNCERTAIN" };
  const serverAnchor = Math.max(issuedAt, highWater);
  const alignedNow = now + (state.clockOffsetMilliseconds ?? 0);
  let trustedNow = Math.max(alignedNow, serverAnchor);
  if (state.observedWallAt !== undefined) {
    if (serverAnchor - state.observedWallAt > MAXIMUM_SERVER_CLOCK_LEAD_MS)
      return { allowed: false, code: "CLOCK_UNCERTAIN" };
    const wallDelta = alignedNow - state.observedWallAt;
    if (state.observedMonotonicAt !== undefined && monotonicNow >= state.observedMonotonicAt) {
      const monotonicDelta = monotonicNow - state.observedMonotonicAt;
      if (Math.abs(wallDelta - monotonicDelta) > MAXIMUM_CLOCK_DRIFT_MS)
        return { allowed: false, code: "CLOCK_UNCERTAIN" };
      trustedNow = Math.max(trustedNow, serverAnchor + monotonicDelta);
    } else {
      if (wallDelta < -MAXIMUM_SERVER_CLOCK_LEAD_MS)
        return { allowed: false, code: "CLOCK_UNCERTAIN" };
      trustedNow = Math.max(trustedNow, serverAnchor + Math.max(0, wallDelta));
    }
  } else if (alignedNow + MAXIMUM_SERVER_CLOCK_LEAD_MS < serverAnchor) {
    return { allowed: false, code: "CLOCK_UNCERTAIN" };
  }
  const ageMilliseconds = Math.max(0, trustedNow - issuedAt);
  if (trustedNow > expiresAt) return { allowed: false, code: "LEASE_EXPIRED", ageMilliseconds };
  if (ageMilliseconds >= 72 * 60 * 60_000)
    return { allowed: true, code: "LEASE_WARNING_72H", ageMilliseconds };
  if (ageMilliseconds >= 24 * 60 * 60_000)
    return { allowed: true, code: "LEASE_WARNING_24H", ageMilliseconds };
  return { allowed: true, code: "READY", ageMilliseconds };
}

let financialWriter: Promise<void> = Promise.resolve();
let syncWriter: Promise<void> = Promise.resolve();

function serializedFinancialWrite<T>(operation: () => Promise<T>): Promise<T> {
  const running = financialWriter.then(operation, operation);
  financialWriter = running.then(
    () => undefined,
    () => undefined,
  );
  return running;
}

function serializedSyncWrite<T>(operation: () => Promise<T>): Promise<T> {
  const running = syncWriter.then(operation, operation);
  syncWriter = running.then(
    () => undefined,
    () => undefined,
  );
  return running;
}

export class EncryptedStudentStore {
  #dataKey: CryptoKey | null = null;
  #replica: StudentReplicaContract | null = null;

  async hasReplica(): Promise<boolean> {
    return Boolean(await readStore<LocalMetadata>(STORE_METADATA, "replica"));
  }

  async freshness(): Promise<string | null> {
    return (await readStore<LocalMetadata>(STORE_METADATA, "replica"))?.synchronizedAt ?? null;
  }

  async initializeWithPin(pin: string, replica: StudentReplicaContract): Promise<void> {
    const salt = randomBytes(16);
    await this.initialize(await pinWrappingKey(pin, salt), "pin", salt, replica);
  }

  async initializeWithPinFromSnapshot(
    pin: string,
    page: ReplicaSnapshotPageContract,
    readiness: {
      readonly persistenceGranted: boolean;
      readonly storageHeadroom: boolean;
      readonly authorized: boolean;
    },
    passkey: PasskeyUnlockMaterial | null = null,
  ): Promise<StudentReplicaContract> {
    if (!(await verifySyncLease(page.lease))) throw new Error("Invalid snapshot lease");
    const probeKey = `durability-${crypto.randomUUID()}`;
    await writeStores([{ store: STORE_SYNC, key: probeKey, value: "probe" }]);
    await deleteStores([{ store: STORE_SYNC, key: probeKey }]);
    const replica: StudentReplicaContract = {
      synchronizedAt: page.serverTime,
      students: page.students,
      ...(page.authorizedActor ? { authorizedActor: page.authorizedActor } : {}),
    };
    const salt = randomBytes(16);
    await this.initialize(
      await pinWrappingKey(pin, salt),
      "pin",
      salt,
      replica,
      {
        lease: page.lease,
        readiness,
      },
      passkey,
    );
    return replica;
  }

  async initializeWithPrf(
    output: Uint8Array<ArrayBuffer>,
    replica: StudentReplicaContract,
  ): Promise<void> {
    await this.initialize(await prfWrappingKey(output), "prf", randomBytes(16), replica);
  }

  async unlockWithPin(pin: string, now = Date.now()): Promise<StudentReplicaContract> {
    const metadata = await readStore<LocalMetadata>(STORE_METADATA, "replica");
    if (!metadata) throw new OfflineUnlockError("CORRUPT_CACHE");
    if (metadata.blocked) throw new OfflineUnlockError("ONLINE_REAUTHORIZATION_REQUIRED");
    if (metadata.retryAfter > now) throw new OfflineUnlockError("THROTTLED");
    if (metadata.method !== "pin") throw new OfflineUnlockError("INVALID_PIN");
    let dataKey: CryptoKey;
    try {
      const wrappingKey = await pinWrappingKey(pin, decode(metadata.salt), metadata.iterations);
      dataKey = await this.unwrapDataKey(wrappingKey, metadata);
    } catch (error) {
      if (error instanceof OfflineUnlockError && error.code === "INVALID_PIN") throw error;
      await this.recordFailure(metadata, now);
      throw new OfflineUnlockError("INVALID_PIN");
    }
    return this.decryptReplica(dataKey, metadata);
  }

  async passkeyUnlockConfiguration(): Promise<PasskeyUnlockConfiguration | null> {
    const passkey = (await readStore<LocalMetadata>(STORE_METADATA, "replica"))?.passkey;
    return passkey
      ? {
          credentialId: passkey.credentialId,
          rpId: passkey.rpId,
          prfSalt: decode(passkey.prfSalt),
        }
      : null;
  }

  async storeDeviceResumeToken(token: string): Promise<void> {
    if (!this.#dataKey || !token) throw new OfflineUnlockError("CORRUPT_CACHE");
    const metadata = await readStore<LocalMetadata>(STORE_METADATA, "replica");
    if (!metadata) throw new OfflineUnlockError("CORRUPT_CACHE");
    const iv = randomBytes(12);
    const ciphertext = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv, additionalData: DEVICE_RESUME_AAD },
      this.#dataKey,
      new TextEncoder().encode(token),
    );
    await writeStores([
      {
        store: STORE_METADATA,
        key: "replica",
        value: {
          ...metadata,
          deviceResume: { iv: encode(iv), ciphertext: encode(new Uint8Array(ciphertext)) },
        } satisfies LocalMetadata,
      },
    ]);
  }

  async deviceResumeToken(): Promise<string | null> {
    if (!this.#dataKey) return null;
    const resume = (await readStore<LocalMetadata>(STORE_METADATA, "replica"))?.deviceResume;
    if (!resume) return null;
    try {
      const plaintext = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: decode(resume.iv), additionalData: DEVICE_RESUME_AAD },
        this.#dataKey,
        decode(resume.ciphertext),
      );
      return new TextDecoder().decode(plaintext);
    } catch {
      throw new OfflineUnlockError("CORRUPT_CACHE");
    }
  }

  async enablePasskeyUnlockWithPin(pin: string, material: PasskeyUnlockMaterial): Promise<void> {
    const metadata = await readStore<LocalMetadata>(STORE_METADATA, "replica");
    if (!metadata || metadata.method !== "pin") throw new OfflineUnlockError("CORRUPT_CACHE");
    const rawDataKey = await this.unwrapRawDataKey(
      await pinWrappingKey(pin, decode(metadata.salt), metadata.iterations),
      metadata,
    );
    const wrapIv = randomBytes(12);
    const wrappedKey = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: wrapIv, additionalData: WRAP_AAD },
      await prfWrappingKey(material.prfOutput),
      rawDataKey,
    );
    await writeStores([
      {
        store: STORE_METADATA,
        key: "replica",
        value: {
          ...metadata,
          schemaVersion: 3,
          passkey: {
            credentialId: material.credentialId,
            rpId: material.rpId,
            prfSalt: encode(material.prfSalt),
            wrapIv: encode(wrapIv),
            wrappedKey: encode(new Uint8Array(wrappedKey)),
          },
        } satisfies LocalMetadata,
      },
    ]);
  }

  async unlockWithPinAfterOnlineReauthorization(pin: string): Promise<StudentReplicaContract> {
    const metadata = await readStore<LocalMetadata>(STORE_METADATA, "replica");
    if (!metadata || metadata.method !== "pin") throw new OfflineUnlockError("CORRUPT_CACHE");
    let dataKey: CryptoKey;
    try {
      const wrappingKey = await pinWrappingKey(pin, decode(metadata.salt), metadata.iterations);
      dataKey = await this.unwrapDataKey(wrappingKey, metadata);
    } catch {
      throw new OfflineUnlockError("INVALID_PIN");
    }
    return this.decryptReplica(dataKey, { ...metadata, blocked: false });
  }

  async unlockWithPrf(
    output: Uint8Array<ArrayBuffer>,
    onlineReauthorized = false,
  ): Promise<StudentReplicaContract> {
    const metadata = await readStore<LocalMetadata>(STORE_METADATA, "replica");
    if (!metadata) throw new OfflineUnlockError("CORRUPT_CACHE");
    if (metadata.blocked && !onlineReauthorized)
      throw new OfflineUnlockError("ONLINE_REAUTHORIZATION_REQUIRED");
    const wrapper = metadata.passkey ?? (metadata.method === "prf" ? metadata : null);
    if (!wrapper) throw new OfflineUnlockError("CORRUPT_CACHE");
    let dataKey: CryptoKey;
    try {
      dataKey = await this.unwrapDataKey(await prfWrappingKey(output), wrapper);
    } catch {
      throw new OfflineUnlockError("CORRUPT_CACHE");
    }
    return this.decryptReplica(dataKey, {
      ...metadata,
      blocked: onlineReauthorized ? false : metadata.blocked,
    });
  }

  lock(): void {
    this.#dataKey = null;
    this.#replica = null;
  }

  async invalidateOfflineAuthorization(): Promise<void> {
    const metadata = await readStore<LocalMetadata>(STORE_METADATA, "replica");
    const syncState = (await readStore<LocalSyncState>(STORE_SYNC, "state")) ?? defaultSyncState();
    await writeStores([
      {
        store: STORE_SYNC,
        key: "state",
        value: { ...syncState, authorized: false },
      },
      ...(metadata
        ? [
            {
              store: STORE_METADATA,
              key: "replica",
              value: { ...metadata, blocked: true },
            },
          ]
        : []),
    ]);
    this.lock();
  }

  search(query: string): readonly StudentProfileContract[] {
    if (!this.#replica) throw new OfflineUnlockError("CORRUPT_CACHE");
    const text = normalizeStudentSearch(query);
    const massar = normalizeMassar(query);
    if (!text) return this.#replica.students;
    return this.#replica.students.filter((student) => {
      const haystack = normalizeStudentSearch(`${student.nameAr} ${student.nameFr}`);
      return (
        student.internalReference.toUpperCase() === query.trim().toUpperCase() ||
        (Boolean(massar) && Boolean(student.massar?.startsWith(massar))) ||
        haystack.includes(text)
      );
    });
  }

  profile(studentId: string): StudentProfileContract | null {
    return this.#replica?.students.find((student) => student.id === studentId) ?? null;
  }

  async replaceReplica(replica: StudentReplicaContract): Promise<void> {
    if (!this.#dataKey) throw new OfflineUnlockError("CORRUPT_CACHE");
    const metadata = await readStore<LocalMetadata>(STORE_METADATA, "replica");
    if (!metadata) throw new OfflineUnlockError("CORRUPT_CACHE");
    const replicaIv = randomBytes(12);
    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: replicaIv, additionalData: REPLICA_AAD },
      this.#dataKey,
      new TextEncoder().encode(JSON.stringify(replica)),
    );
    await writeStores([
      { store: STORE_ENCRYPTED, key: "replica", value: encrypted },
      {
        store: STORE_METADATA,
        key: "replica",
        value: {
          ...metadata,
          replicaIv: encode(replicaIv),
          synchronizedAt: replica.synchronizedAt,
        },
      },
    ]);
    this.#replica = replica;
  }

  async recordSuccessfulSync(
    lease: SignedSyncLeaseContract,
    readiness: {
      readonly persistenceGranted: boolean;
      readonly storageHeadroom: boolean;
      readonly authorized: boolean;
    },
    now = Date.now(),
    monotonicNow = typeof performance === "undefined" ? 0 : performance.now(),
  ): Promise<void> {
    if (!(await verifySyncLease(lease))) throw new Error("Invalid synchronization lease");
    const installation = await installationId();
    if (!installation || lease.installationId !== installation)
      throw new Error("Synchronization lease installation mismatch");
    const probeKey = `durability-${crypto.randomUUID()}`;
    await writeStores([{ store: STORE_SYNC, key: probeKey, value: "probe" }]);
    await deleteStores([{ store: STORE_SYNC, key: probeKey }]);
    const previous = (await readStore<LocalSyncState>(STORE_SYNC, "state")) ?? defaultSyncState();
    await writeStores([
      {
        store: STORE_SYNC,
        key: "state",
        value: nextSyncState(previous, lease, readiness, now, monotonicNow),
      },
    ]);
  }

  async captureReadiness(
    now = Date.now(),
    monotonicNow = typeof performance === "undefined" ? 0 : performance.now(),
  ): Promise<CaptureReadinessContract> {
    if (this.#dataKey && !(await this.verifyFinancialEvidence())) await this.markRecoveryRequired();
    const state = (await readStore<LocalSyncState>(STORE_SYNC, "state")) ?? defaultSyncState();
    const counter = BigInt((await readStore<string>(STORE_COUNTERS, "payment")) ?? "0");
    return assessCaptureReadiness(state, await installationId(), counter, now, monotonicNow);
  }

  async queueItems(): Promise<readonly FinancialQueueItemContract[]> {
    if (!this.#dataKey) throw new OfflineUnlockError("CORRUPT_CACHE");
    const records = await allStoreValues<EncryptedFinancialRecord>(STORE_QUEUE);
    const items = await Promise.all(
      records.map((record) =>
        decryptFinancialRecord<FinancialQueueItemContract>(this.#dataKey!, "queue", record),
      ),
    );
    const payments = await allStoreValues<EncryptedFinancialRecord>(STORE_PAYMENTS);
    const results = await Promise.all(
      payments.map((record) =>
        decryptFinancialRecord<LocalFinancialResultContract>(this.#dataKey!, "payment", record),
      ),
    );
    const references = new Map(
      results.map((result) => [result.payment.id, result.payment.reference]),
    );
    return items
      .map((item) => ({
        ...item,
        reference: item.reference ?? references.get(item.paymentId) ?? "PAY-ERROR-000000",
        attemptCount: item.attemptCount ?? 0,
      }))
      .sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  }

  async unsynchronizedCount(): Promise<number> {
    return (await this.queueItems()).filter((item) => item.state !== "synced").length;
  }

  async syncCursor(): Promise<string> {
    return ((await readStore<LocalSyncState>(STORE_SYNC, "state")) ?? defaultSyncState()).cursor;
  }

  async synchronizeFinancialQueue(
    send: (submission: FinancialSyncSubmissionContract) => Promise<FinancialSyncOutcomeContract>,
    now: () => string = () => new Date().toISOString(),
  ): Promise<readonly FinancialQueueItemContract[]> {
    return serializedSyncWrite(async () => {
      if (!this.#dataKey) throw new OfflineUnlockError("CORRUPT_CACHE");
      for (const item of await this.queueItems()) {
        if (item.state === "synced" || item.state === "rejected") continue;
        const attempt: FinancialQueueItemContract = {
          ...item,
          state: "syncing",
          attemptCount: item.attemptCount + 1,
          lastAttemptAt: now(),
        };
        await this.writeQueueItem(attempt);
        try {
          const paymentRecord = await readStore<EncryptedFinancialRecord>(
            STORE_PAYMENTS,
            item.paymentId,
          );
          const envelopeRecord = await readStore<EncryptedFinancialRecord>(
            STORE_ENVELOPES,
            item.paymentId,
          );
          const auditRecord = await readStore<EncryptedFinancialRecord>(
            STORE_AUDIT,
            item.paymentId,
          );
          if (!paymentRecord || !envelopeRecord || !auditRecord)
            throw new Error("Missing immutable queue evidence");
          const record = await decryptFinancialRecord<LocalFinancialResultContract>(
            this.#dataKey,
            "payment",
            paymentRecord,
          );
          const envelope = await decryptFinancialRecord<StoredEnvelope>(
            this.#dataKey,
            "envelope",
            envelopeRecord,
          );
          const audit = await decryptFinancialRecord<{
            actorUserId: string;
            installationId: string;
          }>(this.#dataKey, "audit", auditRecord);
          const outcome = await send({
            schemaVersion: "phase-03-v1",
            actorUserId: envelope.actorUserId ?? audit.actorUserId,
            installationId: envelope.installationId ?? audit.installationId,
            canonicalBytes: envelope.bytes,
            digest: envelope.digest,
            signature: envelope.signature,
            record,
          });
          await this.writeQueueItem({
            ...attempt,
            state: outcome.status,
            ...(outcome.reasonCode ? { reasonCode: outcome.reasonCode } : {}),
            outcome,
          });
        } catch {
          await this.writeQueueItem({
            ...attempt,
            state: "failed",
            reasonCode: "RETRYABLE_TRANSPORT",
          });
        }
      }
      return this.queueItems();
    });
  }

  async applySnapshotPage(
    page: ReplicaSnapshotPageContract,
    readiness: {
      readonly persistenceGranted: boolean;
      readonly storageHeadroom: boolean;
      readonly authorized: boolean;
    },
  ): Promise<StudentReplicaContract> {
    if (!this.#dataKey || !this.#replica) throw new OfflineUnlockError("CORRUPT_CACHE");
    if (!(await verifySyncLease(page.lease))) throw new Error("Invalid snapshot lease");
    if (page.page === 1 && (await this.unsynchronizedCount()) > 0)
      throw new Error("REHYDRATE_BLOCKED_UNSYNCHRONIZED_EVIDENCE");
    const existing = page.page === 1 ? [] : this.#replica.students;
    const students = new Map(existing.map((student) => [student.id, student]));
    for (const student of page.students) students.set(student.id, student);
    const replica: StudentReplicaContract = {
      synchronizedAt: page.serverTime,
      students: [...students.values()],
      ...(this.#replica.authorizedActor ? { authorizedActor: this.#replica.authorizedActor } : {}),
    };
    await this.writeReplicaAndSync(replica, page.lease, readiness);
    return replica;
  }

  async applyChangePage(
    page: ReplicaChangePageContract,
    readiness?: {
      readonly persistenceGranted: boolean;
      readonly storageHeadroom: boolean;
      readonly authorized: boolean;
    },
  ): Promise<StudentReplicaContract> {
    if (!this.#dataKey || !this.#replica) throw new OfflineUnlockError("CORRUPT_CACHE");
    if (!(await verifySyncLease(page.lease))) throw new Error("Invalid change lease");
    const students = new Map(this.#replica.students.map((student) => [student.id, student]));
    for (const change of page.changes) students.set(change.entityId, change.student);
    const replica: StudentReplicaContract = {
      synchronizedAt: page.serverTime,
      students: [...students.values()],
      ...(this.#replica.authorizedActor ? { authorizedActor: this.#replica.authorizedActor } : {}),
    };
    const previous = (await readStore<LocalSyncState>(STORE_SYNC, "state")) ?? defaultSyncState();
    await this.writeReplicaAndSync(
      replica,
      page.lease,
      readiness ?? {
        persistenceGranted: previous.persistenceGranted,
        storageHeadroom: previous.storageHeadroom,
        authorized: previous.authorized,
      },
    );
    return replica;
  }

  async markRecoveryRequired(): Promise<void> {
    const previous = (await readStore<LocalSyncState>(STORE_SYNC, "state")) ?? defaultSyncState();
    await writeStores([
      { store: STORE_SYNC, key: "state", value: { ...previous, health: "recovery-required" } },
    ]);
  }

  private async writeQueueItem(item: FinancialQueueItemContract): Promise<void> {
    if (!this.#dataKey) throw new OfflineUnlockError("CORRUPT_CACHE");
    await writeStores([
      {
        store: STORE_QUEUE,
        key: item.paymentId,
        value: await encryptFinancialRecord(this.#dataKey, "queue", item),
      },
    ]);
  }

  private async verifyFinancialEvidence(): Promise<boolean> {
    if (!this.#dataKey) return false;
    try {
      const paymentRecords = await allStoreValues<EncryptedFinancialRecord>(STORE_PAYMENTS);
      if (!paymentRecords.length) return true;
      const queueRecords = await allStoreValues<EncryptedFinancialRecord>(STORE_QUEUE);
      const envelopeRecords = await allStoreValues<EncryptedFinancialRecord>(STORE_ENVELOPES);
      if (
        paymentRecords.length !== queueRecords.length ||
        paymentRecords.length !== envelopeRecords.length
      )
        return false;
      const payments = await Promise.all(
        paymentRecords.map((record) =>
          decryptFinancialRecord<LocalFinancialResultContract>(this.#dataKey!, "payment", record),
        ),
      );
      const queue = await Promise.all(
        queueRecords.map((record) =>
          decryptFinancialRecord<FinancialQueueItemContract>(this.#dataKey!, "queue", record),
        ),
      );
      const envelopes = await Promise.all(
        envelopeRecords.map((record) =>
          decryptFinancialRecord<StoredEnvelope>(this.#dataKey!, "envelope", record),
        ),
      );
      const queueIds = new Set(queue.map((item) => item.paymentId));
      const pair = await readStore<InstallationKeys>(STORE_KEYS, "installation-keypair");
      if (!pair) return false;
      const envelopeIds = new Set<string>();
      for (const envelope of envelopes) {
        const bytes = decode(envelope.bytes);
        const parsed = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes)) as {
          id?: unknown;
          reference?: unknown;
          issuanceSnapshot?: unknown;
        };
        if (typeof parsed.id !== "string" || envelopeIds.has(parsed.id)) return false;
        envelopeIds.add(parsed.id);
        const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
        if (encode(digest) !== envelope.digest) return false;
        if (
          !(await crypto.subtle.verify(
            { name: "ECDSA", hash: "SHA-256" },
            pair.publicKey,
            decode(envelope.signature),
            bytes,
          ))
        )
          return false;
        const payment = payments.find((item) => item.payment.id === parsed.id);
        if (
          !payment ||
          parsed.reference !== payment.payment.reference ||
          (parsed.issuanceSnapshot &&
            canonicalJson(parsed.issuanceSnapshot as CanonicalValue) !==
              canonicalJson(payment.document as unknown as CanonicalValue))
        )
          return false;
      }
      return payments.every(
        (payment) => queueIds.has(payment.payment.id) && envelopeIds.has(payment.payment.id),
      );
    } catch {
      return false;
    }
  }

  private async writeReplicaAndSync(
    replica: StudentReplicaContract,
    lease: SignedSyncLeaseContract,
    readiness: {
      readonly persistenceGranted: boolean;
      readonly storageHeadroom: boolean;
      readonly authorized: boolean;
    },
  ): Promise<void> {
    if (!this.#dataKey) throw new OfflineUnlockError("CORRUPT_CACHE");
    if (!(await verifySyncLease(lease))) throw new Error("Invalid synchronization lease");
    const metadata = await readStore<LocalMetadata>(STORE_METADATA, "replica");
    if (!metadata) throw new OfflineUnlockError("CORRUPT_CACHE");
    const replicaIv = randomBytes(12);
    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: replicaIv, additionalData: REPLICA_AAD },
      this.#dataKey,
      new TextEncoder().encode(JSON.stringify(replica)),
    );
    const previous = (await readStore<LocalSyncState>(STORE_SYNC, "state")) ?? defaultSyncState();
    await writeStores([
      { store: STORE_ENCRYPTED, key: "replica", value: encrypted },
      {
        store: STORE_METADATA,
        key: "replica",
        value: {
          ...metadata,
          replicaIv: encode(replicaIv),
          synchronizedAt: replica.synchronizedAt,
        },
      },
      { store: STORE_SYNC, key: "state", value: nextSyncState(previous, lease, readiness) },
    ]);
    this.#replica = replica;
  }

  async finalizedPayments(studentId: string): Promise<readonly LocalFinancialResultContract[]> {
    if (!this.#dataKey) throw new OfflineUnlockError("CORRUPT_CACHE");
    const records = await allStoreValues<EncryptedFinancialRecord>(STORE_PAYMENTS);
    const results = await Promise.all(
      records.map((record) =>
        decryptFinancialRecord<LocalFinancialResultContract>(this.#dataKey!, "payment", record),
      ),
    );
    const queue = new Map((await this.queueItems()).map((item) => [item.paymentId, item]));
    return results
      .filter((result) => result.payment.studentId === studentId)
      .map((result) => {
        const item = queue.get(result.payment.id);
        return item
          ? {
              ...result,
              queueState: item.state,
              ...(item.reasonCode ? { syncReasonCode: item.reasonCode } : {}),
            }
          : result;
      })
      .sort((left, right) =>
        right.payment.clientCreatedAt.localeCompare(left.payment.clientCreatedAt),
      );
  }

  async finalizeFinancialPayment(
    input: FinalizeFinancialPaymentInput,
    hooks: FinancialCommitTestHooks = {},
  ): Promise<LocalFinancialResultContract> {
    return serializedFinancialWrite(async () => {
      if (!this.#dataKey) throw new OfflineUnlockError("CORRUPT_CACHE");
      const dataKey = this.#dataKey;
      const deviceCode = await installationDeviceCode();
      const installation = await installationId();
      if (!deviceCode || !installation) throw new Error("Installation identity is unavailable");
      const readiness = await this.captureReadiness(
        input.readinessNow,
        input.readinessMonotonicNow,
      );
      if (!readiness.allowed) throw new Error(`CAPTURE_BLOCKED:${readiness.code}`);

      const amountMad = wholeMad(input.amountMad);
      if (amountMad <= 0n) throw new Error("Payment amount must be positive");
      const receivedDate = dateOnly(input.receivedDate);
      const today = dateOnly(input.today);
      validateReceivedDate({
        receivedDate,
        today,
        activeFrom: dateOnly(input.activeFrom),
        activeThrough: dateOnly(input.activeThrough),
        ...(input.backdatingReason ? { backdatingReason: input.backdatingReason } : {}),
      });
      const method =
        input.method.method === "cash"
          ? validateMethodIntent({ method: "cash" })
          : input.method.method === "cheque"
            ? validateMethodIntent({ ...input.method })
            : validateMethodIntent({
                ...input.method,
                statedDate: dateOnly(input.method.statedDate),
              });
      if (method.method === "transfer" && method.statedDate > today)
        throw new Error("Transfer stated date cannot be in the future");
      if (
        input.note &&
        (input.note.length > 500 || /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/.test(input.note))
      )
        throw new Error("Invalid payment note");
      const obligations = new Map(
        input.obligations.map((obligation) => [obligation.id, obligation]),
      );
      const existing = await this.finalizedPayments(input.student.id);
      const locallySettled = new Map<string, bigint>();
      for (const finalized of existing) {
        if (
          finalized.payment.settlementStatus !== "settled" ||
          finalized.queueState === "synced" ||
          finalized.queueState === "rejected"
        )
          continue;
        for (const allocation of finalized.payment.allocations)
          locallySettled.set(
            allocation.obligationId,
            (locallySettled.get(allocation.obligationId) ?? 0n) + BigInt(allocation.amountMad),
          );
      }
      const seen = new Set<string>();
      let allocationTotal = 0n;
      for (const allocation of input.allocations) {
        if (seen.has(allocation.obligationId)) throw new Error("Duplicate allocation");
        seen.add(allocation.obligationId);
        const obligation = obligations.get(allocation.obligationId);
        const allocated = wholeMad(allocation.amountMad);
        if (
          !obligation ||
          allocated <= 0n ||
          allocated >
            BigInt(obligation.remainingMad) - (locallySettled.get(allocation.obligationId) ?? 0n)
        )
          throw new Error("Invalid allocation");
        allocationTotal += allocated;
      }
      if (allocationTotal !== amountMad) throw new Error("Allocations must equal payment amount");

      const candidate = (await readStore<string>(STORE_COUNTERS, "payment")) ?? "0";
      const counter = BigInt(candidate) + 1n;
      const clientCreatedAt = input.clientCreatedAt ?? new Date().toISOString();
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(clientCreatedAt))
        throw new Error("Invalid client-created timestamp");
      const observedClock = clockContext(clientCreatedAt, input.trustedObservedAt);
      const payment: FinalizedPayment = {
        id: uuidV4(),
        studentId: input.student.id,
        reference: paymentReference(deviceCode, counter),
        deviceCode,
        counter: counter.toString(),
        amountMad,
        receivedDate,
        clientCreatedAt,
        settlementStatus: method.method === "cash" ? "settled" : "pending",
        finalized: true,
        method,
        allocations: input.allocations.map((allocation) => ({
          obligationId: allocation.obligationId,
          amountMad: wholeMad(allocation.amountMad),
        })),
        ...(input.backdatingReason?.trim()
          ? { backdatingReason: input.backdatingReason.trim() }
          : {}),
        ...(input.note?.trim() ? { note: input.note.trim() } : {}),
      };
      const document = createIssuanceSnapshot({
        id: uuidV4(),
        payment,
        issuedAt: clientCreatedAt,
        student: { ...input.student },
        allocations: payment.allocations.map((allocation) => {
          const obligation = obligations.get(allocation.obligationId)!;
          return {
            obligationId: obligation.id,
            family: obligation.family,
            period: obligation.period,
            labelAr: obligation.labelAr,
            labelFr: obligation.labelFr,
            amountMad: allocation.amountMad.toString(),
            remainingAfterMad: (
              BigInt(obligation.remainingMad) -
              (locallySettled.get(obligation.id) ?? 0n) -
              allocation.amountMad
            ).toString(),
          };
        }),
      });
      const envelopeBytes = canonicalEnvelope(payment, document.id, {
        actorUserId: input.actorUserId,
        installationId: installation,
        deploymentId: input.deploymentId ?? "chams-pay-local-synthetic",
        issuanceSnapshot: document as unknown as CanonicalValue,
      });
      const digestBytes = new Uint8Array(await crypto.subtle.digest("SHA-256", envelopeBytes));
      const digest = encode(digestBytes);
      const signature = await signInstallationValue(new TextDecoder().decode(envelopeBytes));

      const paymentContract: FinalizedPaymentContract = {
        id: payment.id,
        studentId: payment.studentId,
        reference: payment.reference,
        amountMad: payment.amountMad.toString(),
        receivedDate: payment.receivedDate,
        clientCreatedAt: payment.clientCreatedAt,
        method: payment.method,
        settlementStatus: payment.settlementStatus,
        allocations: payment.allocations.map((allocation) => ({
          obligationId: allocation.obligationId,
          amountMad: allocation.amountMad.toString(),
        })),
        finalized: true,
        ...(payment.backdatingReason ? { backdatingReason: payment.backdatingReason } : {}),
        ...(payment.note ? { note: payment.note } : {}),
      };
      const documentContract = document as IssuanceSnapshotContract;
      const result = deepFreeze<LocalFinancialResultContract>({
        payment: paymentContract,
        document: documentContract,
        envelopeDigest: digest,
        envelopeSignature: signature,
        queueState: "queued",
      });
      const encrypted = await Promise.all([
        encryptFinancialRecord(dataKey, "payment", result),
        encryptFinancialRecord(dataKey, "allocations", paymentContract.allocations),
        encryptFinancialRecord(dataKey, "document", documentContract),
        encryptFinancialRecord(dataKey, "envelope", {
          version: "phase-03-v1",
          bytes: encode(envelopeBytes),
          digest,
          signature,
          actorUserId: input.actorUserId,
          installationId: installation,
        } satisfies StoredEnvelope),
        encryptFinancialRecord(dataKey, "queue", {
          paymentId: payment.id,
          reference: payment.reference,
          state: "queued",
          createdAt: clientCreatedAt,
          attemptCount: 0,
        } satisfies FinancialQueueItemContract),
        encryptFinancialRecord(dataKey, "audit", {
          event: "payment.finalized-locally",
          paymentId: payment.id,
          actorUserId: input.actorUserId,
          installationId: installation,
          clientCreatedAt,
          trustedObservedAt: input.trustedObservedAt,
          clockSkewMilliseconds: observedClock.skewMilliseconds,
          clockSkewConspicuous: observedClock.conspicuous,
          result: "success",
        }),
      ]);

      if (hooks.beforeTransaction === "termination")
        throw new Error("Simulated termination before strict transaction");
      if (hooks.beforeTransaction === "counter-conflict")
        await writeStores([
          { store: STORE_COUNTERS, key: "payment", value: (counter + 1n).toString() },
        ]);

      const database = await openDatabase();
      try {
        await new Promise<void>((resolve, reject) => {
          const stores = [
            STORE_COUNTERS,
            STORE_PAYMENTS,
            STORE_ALLOCATIONS,
            STORE_DOCUMENTS,
            STORE_ENVELOPES,
            STORE_QUEUE,
            STORE_AUDIT,
          ];
          const transaction = database.transaction(stores, "readwrite", { durability: "strict" });
          let injectedError: Error | null = null;
          const request = transaction.objectStore(STORE_COUNTERS).get("payment");
          request.onerror = () => transaction.abort();
          request.onsuccess = () => {
            const current = (request.result as string | undefined) ?? "0";
            if (current !== candidate) {
              injectedError = new Error("Conditional payment counter conflict");
              transaction.abort();
              return;
            }
            transaction.objectStore(STORE_COUNTERS).put(counter.toString(), "payment");
            transaction.objectStore(STORE_PAYMENTS).add(encrypted[0], payment.id);
            transaction.objectStore(STORE_ALLOCATIONS).add(encrypted[1], payment.id);
            transaction.objectStore(STORE_DOCUMENTS).add(encrypted[2], document.id);
            transaction.objectStore(STORE_ENVELOPES).add(encrypted[3], payment.id);
            transaction.objectStore(STORE_QUEUE).add(encrypted[4], payment.id);
            transaction.objectStore(STORE_AUDIT).add(encrypted[5], payment.id);
            if (hooks.duringTransaction) {
              injectedError = new Error(`Simulated ${hooks.duringTransaction} failure`);
              transaction.abort();
            }
          };
          transaction.oncomplete = () => resolve();
          transaction.onabort = () =>
            reject(injectedError ?? transaction.error ?? new Error("Strict transaction aborted"));
          transaction.onerror = () => undefined;
        });
      } finally {
        database.close();
      }
      return result;
    });
  }

  private async initialize(
    wrappingKey: CryptoKey,
    method: "pin" | "prf",
    salt: Uint8Array<ArrayBuffer>,
    replica: StudentReplicaContract,
    sync?: {
      readonly lease: SignedSyncLeaseContract;
      readonly readiness: {
        readonly persistenceGranted: boolean;
        readonly storageHeadroom: boolean;
        readonly authorized: boolean;
      };
    },
    passkey: PasskeyUnlockMaterial | null = null,
  ) {
    const dataKey = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, [
      "encrypt",
      "decrypt",
    ]);
    const wrapIv = randomBytes(12);
    const replicaIv = randomBytes(12);
    const rawDataKey = await crypto.subtle.exportKey("raw", dataKey);
    const wrappedKey = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: wrapIv, additionalData: WRAP_AAD },
      wrappingKey,
      rawDataKey,
    );
    const passkeyWrapIv = passkey ? randomBytes(12) : null;
    const passkeyWrappedKey =
      passkey && passkeyWrapIv
        ? await crypto.subtle.encrypt(
            { name: "AES-GCM", iv: passkeyWrapIv, additionalData: WRAP_AAD },
            await prfWrappingKey(passkey.prfOutput),
            rawDataKey,
          )
        : null;
    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: replicaIv, additionalData: REPLICA_AAD },
      dataKey,
      new TextEncoder().encode(JSON.stringify(replica)),
    );
    const metadata: LocalMetadata = {
      schemaVersion: passkey ? 3 : 2,
      method,
      salt: encode(salt),
      iterations: OFFLINE_PIN_ITERATIONS,
      wrapIv: encode(wrapIv),
      wrappedKey: encode(new Uint8Array(wrappedKey)),
      ...(passkey && passkeyWrapIv && passkeyWrappedKey
        ? {
            passkey: {
              credentialId: passkey.credentialId,
              rpId: passkey.rpId,
              prfSalt: encode(passkey.prfSalt),
              wrapIv: encode(passkeyWrapIv),
              wrappedKey: encode(new Uint8Array(passkeyWrappedKey)),
            },
          }
        : {}),
      replicaIv: encode(replicaIv),
      synchronizedAt: replica.synchronizedAt,
      failedAttempts: 0,
      retryAfter: 0,
      blocked: false,
    };
    const values: { store: string; key: string; value: unknown }[] = [
      { store: STORE_ENCRYPTED, key: "replica", value: encrypted },
      { store: STORE_METADATA, key: "replica", value: metadata },
    ];
    if (sync)
      values.push({
        store: STORE_SYNC,
        key: "state",
        value: nextSyncState(defaultSyncState(), sync.lease, sync.readiness),
      });
    await writeStores(values);
    this.#dataKey = dataKey;
    this.#replica = replica;
  }

  private async unwrapRawDataKey(
    wrappingKey: CryptoKey,
    wrapper: Pick<LocalMetadata, "wrapIv" | "wrappedKey">,
  ): Promise<ArrayBuffer> {
    return crypto.subtle.decrypt(
      { name: "AES-GCM", iv: decode(wrapper.wrapIv), additionalData: WRAP_AAD },
      wrappingKey,
      decode(wrapper.wrappedKey),
    );
  }

  private async unwrapDataKey(
    wrappingKey: CryptoKey,
    wrapper: Pick<LocalMetadata, "wrapIv" | "wrappedKey">,
  ): Promise<CryptoKey> {
    const rawKey = await this.unwrapRawDataKey(wrappingKey, wrapper);
    return crypto.subtle.importKey("raw", rawKey, { name: "AES-GCM" }, false, [
      "encrypt",
      "decrypt",
    ]);
  }

  private async decryptReplica(dataKey: CryptoKey, metadata: LocalMetadata) {
    try {
      const encrypted = await readStore<ArrayBuffer>(STORE_ENCRYPTED, "replica");
      if (!encrypted) throw new Error("missing ciphertext");
      const plaintext = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: decode(metadata.replicaIv), additionalData: REPLICA_AAD },
        dataKey,
        encrypted,
      );
      const replica = JSON.parse(new TextDecoder().decode(plaintext)) as StudentReplicaContract;
      if (!Array.isArray(replica.students) || typeof replica.synchronizedAt !== "string")
        throw new Error("invalid replica");
      this.#dataKey = dataKey;
      this.#replica = replica;
      await writeStores([
        {
          store: STORE_METADATA,
          key: "replica",
          value: { ...metadata, failedAttempts: 0, retryAfter: 0, blocked: false },
        },
      ]);
      return replica;
    } catch {
      throw new OfflineUnlockError("CORRUPT_CACHE");
    }
  }

  private async recordFailure(metadata: LocalMetadata, now: number): Promise<void> {
    const failedAttempts = metadata.failedAttempts + 1;
    await writeStores([
      {
        store: STORE_METADATA,
        key: "replica",
        value: {
          ...metadata,
          failedAttempts,
          retryAfter: now + Math.min(30_000, 500 * 2 ** Math.min(failedAttempts - 1, 6)),
          blocked: failedAttempts >= OFFLINE_PIN_FAILURE_LIMIT,
        },
      },
    ]);
  }
}

export async function ensureInstallationKeyPair(): Promise<InstallationKeys> {
  const existing = await readStore<InstallationKeys>(STORE_KEYS, "installation-keypair");
  if (existing) return existing;
  const pair = await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, false, [
    "sign",
    "verify",
  ]);
  await writeStores([{ store: STORE_KEYS, key: "installation-keypair", value: pair }]);
  return pair;
}

export async function installationPublicKey(): Promise<string> {
  const pair = await ensureInstallationKeyPair();
  return encode(new Uint8Array(await crypto.subtle.exportKey("spki", pair.publicKey)));
}

export async function signInstallationValue(value: string): Promise<string> {
  const pair = await ensureInstallationKeyPair();
  const signature = await crypto.subtle.sign(
    { name: "ECDSA", hash: "SHA-256" },
    pair.privateKey,
    new TextEncoder().encode(value),
  );
  return encode(new Uint8Array(signature));
}

export async function installationId(): Promise<string | null> {
  return (await readStore<string>(STORE_METADATA, "installation-id")) ?? null;
}

export async function lastAuthenticatedIdentifier(): Promise<string | null> {
  const id = await installationId();
  if (!id) return null;
  return (await readStore<string>(STORE_METADATA, `last-authenticated-identifier:${id}`)) ?? null;
}

export async function rememberLastAuthenticatedIdentifier(identifier: string): Promise<void> {
  const id = await installationId();
  const normalized = identifier.trim();
  if (!id || !normalized) return;
  await writeStores([
    {
      store: STORE_METADATA,
      key: `last-authenticated-identifier:${id}`,
      value: normalized,
    },
  ]);
}

export async function rememberInstallationId(id: string, deviceCode?: string): Promise<void> {
  await writeStores([
    { store: STORE_METADATA, key: "installation-id", value: id },
    ...(deviceCode
      ? [{ store: STORE_METADATA, key: "installation-device-code", value: deviceCode }]
      : []),
  ]);
}

export async function rememberInstallationDeviceCode(deviceCode: string): Promise<void> {
  if (!/^[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{5}$/.test(deviceCode))
    throw new Error("Invalid installation device code");
  await writeStores([
    { store: STORE_METADATA, key: "installation-device-code", value: deviceCode },
  ]);
}

export async function installationDeviceCode(): Promise<string | null> {
  return (await readStore<string>(STORE_METADATA, "installation-device-code")) ?? null;
}

export async function forgetInstallationIdentity(): Promise<void> {
  await deleteStores([
    { store: STORE_METADATA, key: "installation-id" },
    { store: STORE_METADATA, key: "installation-device-code" },
    { store: STORE_KEYS, key: "installation-keypair" },
  ]);
}

export async function installationProof(
  method: string,
  url: string,
  timestamp = new Date().toISOString(),
) {
  const id = await installationId();
  if (!id) return null;
  const nonce = encode(randomBytes(18));
  const path = new URL(url, location.origin).pathname + new URL(url, location.origin).search;
  const signature = await signInstallationValue(
    `${method.toUpperCase()}\n${path}\n${timestamp}\n${nonce}`,
  );
  return {
    "x-chams-installation": id,
    "x-chams-device-timestamp": timestamp,
    "x-chams-device-nonce": nonce,
    "x-chams-device-signature": signature,
  };
}
